import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../services/api';
import '../styles/escola.css'; // Using standard manager styles
import etgLogo from '../assets/etg.png';
import ufmgLogo from '../assets/Ufmg-sem nome-03.png';

const UploadPage = () => {
    const navigate = useNavigate();
    const [activeTab, setActiveTab] = useState('escola');
    const [formData, setFormData] = useState({
        id_escola: '',
        id_municipio: '', // Added if needed for context, though purely upload here
        id_pavimento: '',
        id_ambiente: '',
        nome_externo: '',
        descricao: '',
        file: null,
        humanizada: false,
        principal: false,
        id_tipo_representacao: ''
    });
    const [loading, setLoading] = useState(false);
    const [mensagem, setMensagem] = useState({ tipo: '', texto: '' });

    // School Search State
    const [escolas, setEscolas] = useState([]);
    const [escolaSearch, setEscolaSearch] = useState('');

    // Pavimentos State
    const [pavimentos, setPavimentos] = useState([]);

    // Fetch pavimentos on mount
    useEffect(() => {
        const fetchPavimentos = async () => {
            try {
                const response = await api.get('/pavimento');
                setPavimentos(response.data.data || []);
            } catch (error) {
                console.error("Erro ao carregar pavimentos:", error);
                mostrarMensagem('error', 'Falha ao carregar lista de pavimentos');
            }
        };
        fetchPavimentos();
    }, []);

    // Filter pavimentos based on selected school
    const pavimentosFiltrados = useMemo(() => {
        if (!formData.id_escola) return [];
        return pavimentos.filter(pav => pav.id_escola?.toString() === formData.id_escola?.toString());
    }, [pavimentos, formData.id_escola]);

    // Ambientes State
    const [ambientes, setAmbientes] = useState([]);

    // Fetch ambientes on mount
    useEffect(() => {
        const fetchAmbientes = async () => {
            try {
                const response = await api.get('/ambiente');
                setAmbientes(response.data.data || []);
            } catch (error) {
                console.error("Erro ao carregar ambientes:", error);
                mostrarMensagem('error', 'Falha ao carregar lista de ambientes');
            }
        };
        fetchAmbientes();
    }, []);

    // Filter ambientes based on selected pavimento
    const ambientesFiltrados = useMemo(() => {
        if (!formData.id_pavimento) return [];
        return ambientes.filter(amb => amb.id_pavimento?.toString() === formData.id_pavimento?.toString());
    }, [ambientes, formData.id_pavimento]);

    // File Type State
    const [tipoArquivo, setTipoArquivo] = useState('imagem'); // 'imagem' ou 'desenho'

    const mostrarMensagem = (tipo, texto) => {
        setMensagem({ tipo, texto });
        setTimeout(() => setMensagem({ tipo: '', texto: '' }), 4000);
    };

    // Fetch schools and Tipos de Representacao on mount
    useEffect(() => {
        const fetchEscolas = async () => {
            try {
                const response = await api.get('/escola');
                setEscolas(response.data.data || []);
            } catch (error) {
                console.error("Erro ao carregar escolas:", error);
                mostrarMensagem('error', 'Falha ao carregar lista de escolas');
            }
        };
        const fetchTiposRepresentacao = async () => {
            try {
                const response = await api.get('/tipo-representacao');
                setTiposRepresentacao(response.data.data || []);
            } catch (error) {
                console.error("Erro ao carregar tipos de representação:", error);
            }
        };
        fetchEscolas();
        fetchTiposRepresentacao();
    }, []);

    const [tiposRepresentacao, setTiposRepresentacao] = useState([]);

    // Filter schools
    const escolasFiltradas = useMemo(() => {
        if (!escolaSearch || escolaSearch.trim() === '') return escolas;
        const searchTerm = escolaSearch.toLowerCase().trim();
        return escolas.filter(escola =>
            escola.nome_escola?.toLowerCase().includes(searchTerm) ||
            escola.id_escola?.toString().toLowerCase().includes(searchTerm) ||
            escola.sigla?.toLowerCase().includes(searchTerm)
        );
    }, [escolas, escolaSearch]);

    const handleInputChange = (e) => {
        const { name, value, type, checked } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value
        }));
    };

    const handleFileChange = (e) => {
        if (e.target.files[0]) {
            setFormData(prev => ({ ...prev, file: e.target.files[0] }));
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);

        try {
            const data = new FormData();
            data.append('file', formData.file);
            data.append('nome_externo', formData.nome_externo);
            data.append('descricao', formData.descricao);

            // Recuperar usuário do localStorage
            const user = JSON.parse(localStorage.getItem('user'));
            if (user && user.id) {
                data.append('usuario', user.id);
            }

            let url = '';
            // Use config/env for base URL or default, ensuring /api prefix matches proxy or backend
            const baseUrl = api.defaults.baseURL;
            const suffix = tipoArquivo === 'desenho' ? '/desenho' : '';

            if (tipoArquivo === 'desenho') {
                if (!formData.id_tipo_representacao) throw new Error('Tipo de representação é obrigatório para desenhos');
                data.append('id_tipo_representacao', formData.id_tipo_representacao);
            }

            if (activeTab === 'escola') {
                if (!formData.id_escola) throw new Error('ID da Escola é obrigatório');
                url = `${baseUrl}/uploads/escolas/${formData.id_escola}${suffix}`;
                data.append('principal', formData.principal);
            } else if (activeTab === 'pavimento') {
                if (!formData.id_pavimento) throw new Error('ID do Pavimento é obrigatório');
                if (!formData.id_escola) throw new Error('ID da Escola é obrigatório para vincular');
                url = `${baseUrl}/uploads/pavimentos/${formData.id_pavimento}${suffix}`;
                data.append('id_escola', formData.id_escola);
                if (tipoArquivo === 'imagem') {
                    data.append('humanizada', formData.humanizada);
                }
            } else if (activeTab === 'ambiente') {
                if (!formData.id_ambiente) throw new Error('ID do Ambiente é obrigatório');
                if (!formData.id_escola) throw new Error('ID da Escola é obrigatório para vincular');
                url = `${baseUrl}/uploads/ambientes/${formData.id_ambiente}${suffix}`;
                data.append('id_escola', formData.id_escola);
            }

            const token = localStorage.getItem('accessToken');
            const response = await fetch(url, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${token}`
                },
                body: data
            });

            const result = await response.json();

            if (!response.ok) {
                throw new Error(result.message || 'Erro no upload');
            }

            mostrarMensagem('success', `${tipoArquivo === 'desenho' ? 'Desenho' : 'Imagem'} enviado com sucesso!`);

            // Resetar form
            setFormData(prev => ({
                ...prev,
                file: null,
                nome_externo: '',
                descricao: '',
                id_pavimento: '',
                id_ambiente: '',
                id_tipo_representacao: ''
                // Keep school selected for convenience? Maybe reset. User said "refaça do 0", simple is better.
            }));
            // Optionally clear file input visually if needed, but React state manages it mainly.

        } catch (error) {
            console.error(error);
            mostrarMensagem('error', error.message || 'Erro ao enviar arquivo.');
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="tipo-ambiente-container">
            <div className="header-controls">
                <button className="btn-back" onClick={() => navigate('/')}>
                    <i className="fas fa-arrow-left"></i>
                </button>
            </div>

            <div className="header">
                <div className="title-container">
                    <img src={etgLogo} alt="ETG Logo" className="logo" />
                    <h1>Upload de Arquivos</h1>
                    <img src={ufmgLogo} alt="UFMG Logo" className="logo" />
                </div>
                <p>Gerenciamento de uploads de Imagens e Desenhos</p>
            </div>

            {mensagem.texto && (
                <div className="status-container">
                    <div className={`status ${mensagem.tipo}`}>{mensagem.texto}</div>
                </div>
            )}

            <div className="controls">
                <form onSubmit={handleSubmit} className="form-inline" style={{ flexDirection: 'column', width: '100%', maxWidth: '1200px', margin: '0 auto' }}>

                    {/* Tabs / Context Selector - Styled as grouped buttons */}
                    <div className="form-group" style={{ flexDirection: 'row', gap: '10px', marginBottom: '20px', justifyContent: 'center' }}>
                        <button
                            type="button"
                            className={`btn ${activeTab === 'escola' ? 'btn-primary' : 'btn-secondary'}`}
                            onClick={() => setActiveTab('escola')}
                            style={{ minWidth: '120px' }}
                        >
                            Escola
                        </button>
                        <button
                            type="button"
                            className={`btn ${activeTab === 'pavimento' ? 'btn-primary' : 'btn-secondary'}`}
                            onClick={() => setActiveTab('pavimento')}
                            style={{ minWidth: '120px' }}
                        >
                            Pavimento
                        </button>
                        <button
                            type="button"
                            className={`btn ${activeTab === 'ambiente' ? 'btn-primary' : 'btn-secondary'}`}
                            onClick={() => setActiveTab('ambiente')}
                            style={{ minWidth: '120px' }}
                        >
                            Ambiente
                        </button>
                    </div>

                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '15px', border: '1px solid #dee2e6', padding: '20px', borderRadius: '5px', backgroundColor: '#fff' }}>

                        {/* File Type Toggle */}
                        {/* File Type Dropdown */}
                        <div className="form-group" style={{ width: '200px' }}>
                            <label>Tipo de Arquivo:<span style={{ color: 'red' }}>*</span></label>
                            <select
                                value={tipoArquivo}
                                onChange={(e) => setTipoArquivo(e.target.value)}
                                style={{ padding: '8px', border: '1px solid #ced4da', borderRadius: '4px', width: '100%' }}
                            >
                                <option value="imagem">Imagem (JPG, PNG)</option>
                                <option value="desenho">Desenho (PDF, Planta)</option>
                            </select>
                        </div>

                        {/* File Input */}
                        <div className="form-group" style={{ flex: '1', minWidth: '300px' }}>
                            <label>Selecione o Arquivo:<span style={{ color: 'red' }}>*</span></label>
                            <input
                                type="file"
                                onChange={handleFileChange}
                                accept={tipoArquivo === 'imagem' ? "image/*" : ".pdf,.dwg,.dxf,image/*"}
                                required
                                style={{ padding: '5px', border: '1px solid #ced4da', borderRadius: '4px', width: '100%' }}
                            />
                        </div>

                        {tipoArquivo === 'desenho' && (
                            <div className="form-group" style={{ width: '250px' }}>
                                <label>Tipo Representação:<span style={{ color: 'red' }}>*</span></label>
                                <select
                                    name="id_tipo_representacao"
                                    value={formData.id_tipo_representacao}
                                    onChange={handleInputChange}
                                    required
                                    style={{ padding: '8px', border: '1px solid #ced4da', borderRadius: '4px', width: '100%' }}
                                >
                                    <option value="">Selecione...</option>
                                    {tiposRepresentacao.map(tr => (
                                        <option key={tr.id_tipo_representacao} value={tr.id_tipo_representacao}>
                                            {tr.descricao}
                                        </option>
                                    ))}
                                </select>
                            </div>
                        )}

                        {/* School Selection */}
                        <div className="form-group" style={{ width: '100%' }}>
                            <label>Escola (Busca):<span style={{ color: 'red' }}>*</span></label>
                            <input
                                type="text"
                                name="escola_search"
                                value={escolaSearch}
                                onChange={(e) => {
                                    setEscolaSearch(e.target.value);
                                    const escolaEncontrada = escolas.find(esc => esc.nome_escola === e.target.value);
                                    if (escolaEncontrada) {
                                        setFormData(prev => ({ ...prev, id_escola: escolaEncontrada.id_escola }));
                                    } else {
                                        setFormData(prev => ({ ...prev, id_escola: '' }));
                                    }
                                }}
                                list="escolas-list-upload"
                                required
                                placeholder="Digite o nome da escola..."
                                autoComplete="off"
                                style={{ padding: '8px', border: '1px solid #ced4da', borderRadius: '4px', width: '100%' }}
                            />
                            <datalist id="escolas-list-upload">
                                {escolasFiltradas.map(item => (
                                    <option key={item.id_escola} value={item.nome_escola} />
                                ))}
                            </datalist>
                            {formData.id_escola && <small style={{ color: 'green', marginTop: '2px' }}>INEP Selecionado: {formData.id_escola}</small>}
                        </div>

                        {/* Context specific inputs */}
                        {activeTab === 'pavimento' && (
                            <div className="form-group" style={{ minWidth: '200px' }}>
                                <label>Pavimento:<span style={{ color: 'red' }}>*</span></label>
                                <select
                                    name="id_pavimento"
                                    value={formData.id_pavimento}
                                    onChange={handleInputChange}
                                    required
                                    style={{ padding: '8px', border: '1px solid #ced4da', borderRadius: '4px', width: '100%' }}
                                    disabled={!formData.id_escola}
                                >
                                    <option value="">Selecione um Pavimento</option>
                                    {pavimentosFiltrados.map(pav => (
                                        <option key={pav.id_pavimento} value={pav.id_pavimento}>
                                            {pav.descr_pavimento || pav.id_pavimento}
                                        </option>
                                    ))}
                                </select>
                                {!formData.id_escola && <small style={{ color: 'orange' }}>Selecione uma escola primeiro</small>}
                                {tipoArquivo === 'imagem' && (
                                    <label style={{ display: 'flex', alignItems: 'center', gap: '5px', fontWeight: 'normal', cursor: 'pointer', marginTop: '10px' }}>
                                        Imagem Humanizada?
                                        <input
                                            type="checkbox"
                                            name="humanizada"
                                            checked={formData.humanizada}
                                            onChange={handleInputChange}
                                        />
                                    </label>
                                )}
                            </div>
                        )}



                        {activeTab === 'ambiente' && (
                            <>
                                <div className="form-group" style={{ minWidth: '200px' }}>
                                    <label>Pavimento (Filtro):<span style={{ color: 'red' }}>*</span></label>
                                    <select
                                        name="id_pavimento"
                                        value={formData.id_pavimento}
                                        onChange={handleInputChange}
                                        required
                                        style={{ padding: '8px', border: '1px solid #ced4da', borderRadius: '4px', width: '100%' }}
                                        disabled={!formData.id_escola}
                                    >
                                        <option value="">Selecione um Pavimento</option>
                                        {pavimentosFiltrados.map(pav => (
                                            <option key={pav.id_pavimento} value={pav.id_pavimento}>
                                                {pav.descr_pavimento || pav.id_pavimento}
                                            </option>
                                        ))}
                                    </select>
                                </div>

                                <div className="form-group" style={{ minWidth: '200px' }}>
                                    <label>Ambiente:<span style={{ color: 'red' }}>*</span></label>
                                    <select
                                        name="id_ambiente"
                                        value={formData.id_ambiente}
                                        onChange={handleInputChange}
                                        required
                                        style={{ padding: '8px', border: '1px solid #ced4da', borderRadius: '4px', width: '100%' }}
                                        disabled={!formData.id_pavimento}
                                    >
                                        <option value="">Selecione um Ambiente</option>
                                        {ambientesFiltrados.map(amb => (
                                            <option key={amb.id_ambiente} value={amb.id_ambiente}>
                                                {amb.nome_ambiente || amb.codigo_ambiente || amb.id_ambiente}
                                            </option>
                                        ))}
                                    </select>
                                    {!formData.id_pavimento && <small style={{ color: 'orange' }}>Selecione um pavimento primeiro</small>}
                                </div>
                            </>
                        )}

                        {activeTab === 'escola' && (
                            <div className="form-group" style={{ display: 'flex', alignItems: 'center', paddingTop: '25px', minWidth: '150px' }}>
                                <label style={{ display: 'flex', alignItems: 'center', gap: '5px', fontWeight: 'normal', cursor: 'pointer' }}>
                                    {tipoArquivo === 'imagem' ? 'Imagem Principal?' : 'Desenho Principal?'}
                                    <input
                                        type="checkbox"
                                        name="principal"
                                        checked={formData.principal}
                                        onChange={handleInputChange}
                                    />
                                </label>
                            </div>
                        )}



                    </div>

                    <div className="form-actions-row">
                        <div className="form-buttons">
                            <button type="submit" className="btn btn-success" disabled={loading}>
                                {loading ? '⏳ Enviando...' : '➕ Enviar Upload'}
                            </button>
                            <button type="button" className="btn btn-secondary" onClick={() => navigate('/')} disabled={loading}>
                                ❌ Cancelar
                            </button>
                        </div>
                        <div className="info-campos-obrigatorios">
                            <i className="fas fa-info-circle"></i>
                            <span>* indica que os campos são obrigatorios.</span>
                        </div>
                    </div>

                </form>
            </div>
        </div>
    );
};

export default UploadPage;
